import React, { useState, useEffect } from "react";
import api from "../api";
import { Ban, AlertTriangle, ShieldCheck, Siren, Scale, Lock, Info, Search, ClipboardList, FlaskConical } from "lucide-react";


/* Aurora bans emoji in UI, but the feed is backend-authored and ships one per
   item plus a raw hex colour - six of which are foreign accents (cyan #00D4FF
   and purple #BF5FFF among them). Rather than wait on a backend deploy, the
   emoji and the hex are both dropped at the boundary here and re-derived from
   the item's SEMANTICS, so future feed copy renders in-brand automatically. */
const FEED_ICON = {
  "\u{1F6AB}": Ban, "\u26A0": AlertTriangle, "\u{1F6E1}": ShieldCheck, "\u{1F6A8}": Siren,
  "\u2696": Scale, "\u{1F510}": Lock, "\u{1F4A1}": Info, "\u{1F50D}": Search,
  "\u{1F4CB}": ClipboardList, "\u{1F9EA}": FlaskConical,
};
const TYPE_ICON = { blocked: Ban, stat: ShieldCheck, warning: AlertTriangle };

function feedIcon(item) {
  const key = (item.icon || "").replace(/\uFE0F/g, "");
  return FEED_ICON[key] || TYPE_ICON[item.type] || ShieldCheck;
}
/* Severity -> token. Never the raw colour the API sends. */
function feedTone(item) {
  if (item.type === "blocked") return "var(--au-error)";
  if (item.type === "warning") return "var(--au-warn)";
  return "var(--au-accent-ink)";
}

/**
 * Live ticker marquee of the feed as compact Aurora pills. Two presentations:
 *  - variant="bar"  (default): full-width top bar — pinned LIVE chip + scrolling pills.
 *  - variant="pill": a compact hero pill — a LIVE dot + the same feed scrolling inside.
 * Data mapping unchanged; only the layout differs.
 */
export default function Ticker({ variant = "bar" }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const load = () => {
      api.get("/api/ticker/feed").then(r => {
        if (r.data?.feed?.length > 0) setItems(r.data.feed);
      }).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000); // Refresh every minute
    return () => clearInterval(id);
  }, []);

  if (items.length === 0) return null;

  // Duplicate the feed so translateX(-50%) loops seamlessly.
  const loop = [...items, ...items];
  const duration = Math.max(34, items.length * 5);

  const keyframes = (
    <style>{`
      @keyframes tickerMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      .au-marquee-track { display: flex; width: max-content; animation: tickerMarquee ${duration}s linear infinite; will-change: transform; }
      .au-marquee-viewport:hover .au-marquee-track { animation-play-state: paused; }
      @media (prefers-reduced-motion: reduce) { .au-marquee-track { animation: none; } }
    `}</style>
  );

  // ── Compact hero pill ────────────────────────────────────────────────
  if (variant === "pill") {
    return (
      <div style={{ display: "inline-flex", maxWidth: "100%", marginBottom: 18 }}>
        {keyframes}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 9, maxWidth: "100%",
          padding: "5px 8px", borderRadius: "var(--au-r-pill)",
          border: "1px solid var(--au-hair)", background: "var(--au-glass)",
          WebkitBackdropFilter: "var(--au-blur)", backdropFilter: "var(--au-blur)",
        }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0, paddingLeft: 4 }}>
            <span className="au-led" />
            <span style={{ fontSize: 11, fontWeight: 700, color: "var(--au-accent-ink)", letterSpacing: 1.2 }}>LIVE</span>
          </span>
          <span style={{ width: 1, height: 14, background: "var(--au-hair)", flexShrink: 0 }} />
          <div className="au-marquee-viewport" style={{
            width: "min(360px, 60vw)", overflow: "hidden", position: "relative",
            WebkitMaskImage: "linear-gradient(90deg, transparent, #000 16px, #000 calc(100% - 20px), transparent)",
            maskImage: "linear-gradient(90deg, transparent, #000 16px, #000 calc(100% - 20px), transparent)",
          }}>
            <div className="au-marquee-track">
              {loop.map((item, i) => (
                <span key={i} style={{
                  display: "inline-flex", alignItems: "center", gap: 7, flexShrink: 0,
                  marginRight: 22, whiteSpace: "nowrap", fontFamily: "var(--au-font)",
                }}>
                  {React.createElement(feedIcon(item), { size: 13, "aria-hidden": true,
                    style: { color: feedTone(item), flex: "none" } })}
                  <span style={{ fontSize: "var(--au-fs-meta)", fontWeight: 600, color: "var(--au-text-2)", lineHeight: 1 }}>{item.text}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Full-width top bar (default) ─────────────────────────────────────
  return (
    <div style={{
      background: "var(--au-glass)",
      WebkitBackdropFilter: "var(--au-blur)", backdropFilter: "var(--au-blur)",
      borderTop: "1px solid var(--au-hair)", borderBottom: "1px solid var(--au-hair)",
      padding: "9px 24px", position: "relative", zIndex: 50, overflow: "hidden",
    }}>
      {keyframes}
      <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", alignItems: "center", gap: 14 }}>
        <div className="au-chip au-chip-status" style={{ flexShrink: 0, padding: "3px 10px", borderRadius: "var(--au-r-sm)" }}>
          <span className="au-led" />
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--au-accent-ink)", letterSpacing: 1.6 }}>LIVE</span>
        </div>
        <div className="au-marquee-viewport" style={{
          flex: 1, overflow: "hidden", position: "relative",
          WebkitMaskImage: "linear-gradient(90deg, transparent, #000 42px, #000 calc(100% - 42px), transparent)",
          maskImage: "linear-gradient(90deg, transparent, #000 42px, #000 calc(100% - 42px), transparent)",
        }}>
          <div className="au-marquee-track">
            {loop.map((item, i) => (
              <span key={i} style={{
                display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0, marginRight: 10,
                padding: "4px 13px", borderRadius: "var(--au-r-pill)",
                border: "1px solid var(--au-hair)", background: "var(--au-glass-strong)",
                whiteSpace: "nowrap", fontFamily: "var(--au-font)",
              }}>
                {React.createElement(feedIcon(item), { size: 14, "aria-hidden": true,
                  style: { color: feedTone(item), flex: "none" } })}
                <span style={{ fontSize: "var(--au-fs-meta)", fontWeight: 600, color: "var(--au-text-2)", lineHeight: 1 }}>{item.text}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
